import {
  useCollectionsStore,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
  setSelectedSchemaEntry,
  setCollections,
} from '../../store/collectionsStore';
import {
  useTabsStore,
  setRequestTabs,
  setActiveRequestTabId,
  setActiveTabType,
  setRequestTabSyncRevision,
} from '../../store/tabsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import { rekeyScriptEnvOverlaySources } from '../../utils/scriptEnvOverlay';
import { setShowCollectionSelectionModal, setPendingRequestData, setPendingRequestTabId } from '../../store/uiModalStore';
import { requireWritableWorkspace } from '../../plans/localGate';
import { requireApiCapacity } from '../../plans/localApiUsage';
import { apiRequestService } from '../../services/apiRequestService';
import { isCloudRestWorkspace } from '../../utils/workspaceStorage';
import { resolveRequestRecord, findParentCollectionId, findRequestTabForRequest } from '../../utils/requestResolve';
import { prepareRequestBodyForApi } from '../../utils/requestPayload';
import { mapApiRequestToSidebar } from '../../utils/requestMappers';
import { resolvedRequestName } from '../../utils/deriveRequestNameFromUrl';
import { stripBlankKvRows } from '../../utils/kvRows';
import { generateLocalEntityId } from '../../utils/localIds';
import { defaultRequestAuth } from '../../utils/requestAuth';
import { normalizeRequestBodyMode } from '../../utils/requestBodyMode';
import { getFormDataFieldsFromRequest, normalizeFormDataFields } from '../../utils/multipart';
import { kindToSave } from '../../utils/requestKinds';
import { protocolFieldsOf } from '../../utils/requestProtocolFields';

/** Opening a request in the editor and saving it. */
export function useRequestSelectSave({
  getLiveEditorStateForRequest,
  toastT,
  createNewRequestTab,
  selectRequestInFlightRef,
}) {
  // ---- Editor record mapping + selection (hot path) ------------------------
  const editorDataFromRequestRecord = (record, extras = {}) => {
    const mapped = record?.body_type != null ? mapApiRequestToSidebar(record) : record;
    const bodyMode = normalizeRequestBodyMode(mapped);
    const formDataFields = normalizeFormDataFields(getFormDataFieldsFromRequest(mapped));
    return {
      id: mapped.id,
      name: mapped.name,
      method: mapped.method || 'GET',
      url: mapped.url || '',
      headers:
        mapped.headers?.length > 0 ? mapped.headers : [{ key: '', value: '', enabled: true }],
      params:
        mapped.params?.length > 0 ? mapped.params : [{ key: '', value: '', enabled: true }],
      cookies:
        mapped.cookies?.length > 0 ? mapped.cookies : [{ key: '', value: '', enabled: true }],
      body: mapped.body || '',
      bodyType: bodyMode.bodyType,
      rawBodyLanguage: bodyMode.rawBodyLanguage,
      formDataFields:
        bodyMode.bodyType === 'form-data' || bodyMode.bodyType === 'urlencoded'
          ? formDataFields
          : undefined,
      auth: mapped.auth || defaultRequestAuth(),
      description: mapped.description || '',
      preRequestScript: mapped.preRequestScript ?? '',
      testScript: mapped.testScript ?? '',
      mock: mapped.mock ?? { responseExamples: [] },
      type: mapped.type || 'http',
      // gRPC / WebSocket settings (card 5258c71d): opening from the tree used to drop them.
      ...protocolFieldsOf(mapped),
      ...extras,
    };
  };

  const selectRequest = async (request) => {
    if (!request?.id) return;

    const requestKey = String(request.id);
    const inFlight = selectRequestInFlightRef.current.get(requestKey);
    if (inFlight) {
      await inFlight;
      return;
    }

    const prevSchemaEntry = useCollectionsStore.getState().selectedSchemaEntry;
    const prevSelectedCollection = useCollectionsStore.getState().selectedCollection;
    const prevSelectedFolder = useCollectionsStore.getState().selectedFolder;

    const runSelect = async () => {
      setSelectedCollection(null);
      setSelectedFolder(null);
      setSelectedSchemaEntry(null);
      const ws = useWorkspaceStore.getState().currentWorkspace;
      const authed = useAuthStore.getState().isAuthenticated;
      const collections = useCollectionsStore.getState().collections;
      const cloud = isCloudRestWorkspace(ws, authed);

      const fetchFullRecord = cloud
        ? (id) => apiRequestService.getRequest(id)
        : undefined;

      const resolved = await resolveRequestRecord({
        requestId: request.id,
        collections,
        requestTabs: useTabsStore.getState().requestTabs,
        activeRequestTabId: useTabsStore.getState().activeRequestTabId,
        fetchFullRecord,
        liveEditorState: getLiveEditorStateForRequest(request.id),
      });

      const recordForEditor = resolved ?? request;
      const collectionId =
        recordForEditor.collection_id ??
        recordForEditor.collection?.id ??
        recordForEditor.collectionId ??
        findParentCollectionId(collections, request.id);

      const requestData = editorDataFromRequestRecord(recordForEditor, {
        collectionId,
        originalId: request.id,
        isNewRequest: false,
      });

      const existingTab = findRequestTabForRequest(
        useTabsStore.getState().requestTabs,
        request.id,
        useTabsStore.getState().activeRequestTabId
      );

      const applyToTab = (tabId) => {
        const { id: _rid, ...tabUpdateData } = requestData;
        setRequestTabs((prev) =>
          prev.map((tab) =>
            tab.id === tabId
              ? {
                  ...tab,
                  ...tabUpdateData,
                  id: tab.id,
                  originalId: request.id,
                  isNewRequest: false,
                  hasUnsavedChanges: false,
                }
              : tab
          )
        );
        setActiveRequestTabId(tabId);
        setActiveTabType('request');
        setSelectedRequest(recordForEditor);
        setRequestTabSyncRevision((n) => n + 1);
      };

      if (existingTab) {
        setActiveRequestTabId(existingTab.id);
        setActiveTabType('request');
        setSelectedCollection(null);
        setSelectedFolder(null);
        setSelectedRequest(recordForEditor);
        return;
      }

      const tabAfterFetch = findRequestTabForRequest(
        useTabsStore.getState().requestTabs,
        request.id,
        useTabsStore.getState().activeRequestTabId
      );
      if (tabAfterFetch) {
        setActiveRequestTabId(tabAfterFetch.id);
        setActiveTabType('request');
        setSelectedCollection(null);
        setSelectedFolder(null);
        setSelectedRequest(recordForEditor);
        return;
      }

      const currentTab = useTabsStore.getState().requestTabs.find(
        (t) => t.id === useTabsStore.getState().activeRequestTabId
      );
      if (
        currentTab &&
        !currentTab.url &&
        !currentTab.hasUnsavedChanges &&
        currentTab.isNewRequest &&
        currentTab.name === 'New Request'
      ) {
        applyToTab(currentTab.id);
        return;
      }

      createNewRequestTab(requestData);
      setSelectedRequest(recordForEditor);
      setRequestTabSyncRevision((n) => n + 1);
    };

    const promise = runSelect().catch((error) => {
      console.error('Failed to load request details:', error);
      toastT('toast.loadRequestFailed', 'error', { message: error.message });
      if (prevSchemaEntry != null) {
        setSelectedSchemaEntry((current) => current == null ? prevSchemaEntry : current);
      }
      if (prevSelectedCollection != null) {
        setSelectedCollection((current) => current == null ? prevSelectedCollection : current);
      }
      if (prevSelectedFolder != null) {
        setSelectedFolder((current) => current == null ? prevSelectedFolder : current);
      }
    });

    selectRequestInFlightRef.current.set(requestKey, promise);
    try {
      await promise;
    } finally {
      selectRequestInFlightRef.current.delete(requestKey);
    }
  };

  // ---- Save request (local snapshot or cloud API) --------------------------
  const saveRequest = async (requestData, opts = {}) => {
    const { requestTabs, activeRequestTabId } = useTabsStore.getState();
    const collections = useCollectionsStore.getState().collections;
    const selectedRequest = useCollectionsStore.getState().selectedRequest;
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    // Plans v2: nothing is saved into an own cloud workspace while it is read-only.
    if (!requireWritableWorkspace(currentWorkspace)) return;

    const rawTab = requestTabs.find((tab) => tab.id === activeRequestTabId);
    /** After picking a destination, state may not have flushed before save reruns — merge picked ids */
    const currentTab =
      rawTab && (opts.pickedCollectionId != null || opts.pickedFolderId !== undefined)
        ? {
            ...rawTab,
            ...(opts.pickedCollectionId != null ? { collectionId: opts.pickedCollectionId } : {}),
            ...(opts.pickedFolderId !== undefined ? { folderId: opts.pickedFolderId } : {}),
          }
        : rawTab;

    // Resolve against the CURRENT collections tree, not the selectedRequest
    // snapshot (taken at tab-switch time): in team workspaces realtime patches
    // collections in place, and the PUT below echoes folder_id/settings/
    // sort_order back — resolving stale would revert a teammate's move.
    // Fall back to selectedRequest only when it's the SAME request the tab
    // targets (e.g. collections hasn't caught up yet after a rejoin resync) —
    // never treat "not found yet" as "deleted" while a matching snapshot is
    // still in hand, and never resurrect an unrelated stale selectedRequest.
    const resolvedRequest = (() => {
      const targetId = currentTab?.originalId ?? selectedRequest?.id;
      if (targetId != null) {
        for (const col of collections) {
          const found = (col.requests || []).find((r) => String(r.id) === String(targetId));
          if (found) return found;
        }
        if (selectedRequest && String(selectedRequest.id) === String(targetId)) {
          return selectedRequest;
        }
      }
      return null;
    })();

    // If it's a new request without a collection, prompt for collection selection
    if (currentTab?.isNewRequest && !currentTab.collectionId) {
      // Store the request data to save after collection is selected
      setPendingRequestData(requestData);
      setPendingRequestTabId(currentTab.id);

      // Always open the save-request (collection selection) modal — it supports
      // creating a collection inline (allowCreate), so an empty tree no longer
      // diverts to the standalone Create Collection modal. That modal is only
      // for the explicit "+"/context "New collection" action.
      setShowCollectionSelectionModal(true);
      return;
    }

    if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
      // Local workspace (guest or signed-in): persist in snapshot only
      if (currentTab?.isNewRequest) {
        const hasValidCollectionInState = collections.some(
          (collection) => String(collection.id) === String(currentTab.collectionId)
        );
        if (!hasValidCollectionInState) {
          setPendingRequestData(requestData);
          setPendingRequestTabId(currentTab.id);
          setShowCollectionSelectionModal(true);
          toastT('toast.collectionNoLongerExists', 'warning');
          return;
        }

        // Plans v2: Free saves 20 requests (across its local workspaces).
        if (!(await requireApiCapacity('requests'))) return;
        // Create new request locally
        const resolvedName = resolvedRequestName(requestData.name, requestData.url);
        const headerRows = stripBlankKvRows(requestData.headers);
        const paramRows = stripBlankKvRows(requestData.params);
        const cookieRows = stripBlankKvRows(requestData.cookies);
        const newRequest = {
          id: generateLocalEntityId('req'),
          name: resolvedName,
          method: requestData.method,
          url: requestData.url,
          headers: headerRows.length > 0 ? headerRows : [{ key: '', value: '', enabled: true }],
          params: paramRows.length > 0 ? paramRows : [{ key: '', value: '', enabled: true }],
          cookies: cookieRows.length > 0 ? cookieRows : [{ key: '', value: '', enabled: true }],
          body: requestData.body || '',
          bodyType: requestData.bodyType || 'json',
          rawBodyLanguage: requestData.rawBodyLanguage || 'text',
          formDataFields: requestData.formDataFields ?? undefined,
          auth: requestData.auth || defaultRequestAuth(),
          mock: requestData.mock ?? { responseExamples: [] },
          preRequestScript: requestData.preRequestScript ?? '',
          testScript: requestData.testScript ?? '',
          type: requestData.type || currentTab.type || 'http',
          ...protocolFieldsOf({ ...currentTab, ...requestData }),
          folder_id: currentTab.folderId || null,
        };

        // Add to collection
        setCollections((prev) => prev.map((collection) =>
          collection.id === currentTab.collectionId
            ? { ...collection, requests: [...(collection.requests || []), newRequest] }
            : collection
        ));

        // What this tab's scripts set while unsaved now belongs to the saved
        // request, so its next send still replaces it (scriptEnvOverlay).
        rekeyScriptEnvOverlaySources(activeRequestTabId, newRequest.id);
        // Update tab to no longer be a new request
        setRequestTabs((prev) => prev.map((tab) =>
          tab.id === activeRequestTabId
            ? { ...tab, ...requestData, id: tab.id, name: resolvedName, hasUnsavedChanges: false, isNewRequest: false, originalId: newRequest.id }
            : tab
        ));

        toastT('toast.requestCreated', 'success', { name: newRequest.name });
      } else if (resolvedRequest) {
        // Update existing request locally
        setCollections((prev) => prev.map((collection) => ({
          ...collection,
          requests: (collection.requests || []).map((request) =>
            request.id === resolvedRequest.id
              ? { ...request, ...requestData }
              : request
          ),
        })));

        setRequestTabs((prev) => prev.map((tab) =>
          tab.id === activeRequestTabId
            ? { ...tab, ...requestData, id: tab.id, hasUnsavedChanges: false }
            : tab
        ));

        toastT('toast.requestSaved', 'success', { name: resolvedRequest.name });
      } else {
        toastT('toast.requestNoLongerExists', 'warning');
      }
      return;
    }

    // Cloud workspace: persist via API
    if (currentTab?.isNewRequest) {
      // Create new request via backend
      try {
        const hasValidCollectionInState = collections.some(
          (collection) => String(collection.id) === String(currentTab.collectionId)
        );

        if (!hasValidCollectionInState) {
          setPendingRequestData(requestData);
          setPendingRequestTabId(currentTab.id);
          setShowCollectionSelectionModal(true);
          toastT('toast.selectBackendCollection', 'warning');
          return;
        }

        const newRequest = await apiRequestService.createRequest(
          prepareRequestBodyForApi(
            { ...requestData, folder_id: currentTab.folderId ?? null, type: kindToSave(requestData, currentTab) },
            currentTab.collectionId,
            resolvedRequestName(requestData.name, requestData.url)
          )
        );

        // Add to collection in state
        const mappedNew = mapApiRequestToSidebar(newRequest);
        const folderId = mappedNew.folder_id ?? currentTab.folderId ?? null;
        const mappedNewWithFolder = folderId
          ? { ...mappedNew, folder_id: folderId, settings: { ...(mappedNew.settings || {}), folder_id: folderId } }
          : mappedNew;
        // Functional update: realtime broadcasts may have patched collections
        // while the create round-trip was in flight; a captured-snapshot write
        // here would silently revert them.
        setCollections((prev) => prev.map((collection) =>
          collection.id === currentTab.collectionId
            ? {
                ...collection,
                requests: [...(collection.requests || []), mappedNewWithFolder],
              }
            : collection
        ));

        // What this tab's scripts set while unsaved now belongs to the saved
        // request, so its next send still replaces it (scriptEnvOverlay).
        rekeyScriptEnvOverlaySources(activeRequestTabId, newRequest.id);
        // Update tab to no longer be a new request
        setRequestTabs((prev) => prev.map((tab) =>
          tab.id === activeRequestTabId
            ? { ...tab, ...requestData, id: tab.id, name: newRequest.name, hasUnsavedChanges: false, isNewRequest: false, originalId: newRequest.id }
            : tab
        ));

        toastT('toast.requestCreated', 'success', { name: newRequest.name });
      } catch (error) {
        console.error('Failed to create request:', error);
        toastT('toast.createRequestFailed', 'error', { message: error.message });
      }
    } else if (resolvedRequest) {
      try {
        const updateData = prepareRequestBodyForApi(
          {
            ...requestData,
            folder_id: resolvedRequest.folder_id,
            settings: resolvedRequest.settings,
            sort_order: resolvedRequest.sort_order ?? 0,
            type: kindToSave(requestData, resolvedRequest, currentTab),
          },
          resolvedRequest.collection_id || currentTab?.collectionId,
          requestData.name || resolvedRequest.name
        );

        // Save to backend
        const updatedRequest = await apiRequestService.updateRequest(resolvedRequest.id, updateData);

        // Update existing request in collections. Functional update: realtime
        // broadcasts may have patched collections during the PUT round-trip.
        setCollections((prev) => prev.map((collection) => ({
          ...collection,
          requests: (collection.requests || []).map((request) =>
            request.id === resolvedRequest.id
              ? {
                  ...request,
                  ...requestData,
                  name: updatedRequest.name,
                  description: updatedRequest.description,
                  method: updatedRequest.method,
                  url: updatedRequest.url,
                  headers: updatedRequest.headers || [],
                  params: updatedRequest.params || [],
                  cookies: updatedRequest.cookies || [],
                  body: updatedRequest.body || '',
                  bodyType: updatedRequest.body_type || 'json',
                  rawBodyLanguage: requestData.rawBodyLanguage,
                  settings: updatedRequest.settings || request.settings,
                  auth: updatedRequest.auth || {},
                  updated_at: updatedRequest.updated_at,
                }
              : request
          ),
        })));

        // Mark current tab as saved
        setRequestTabs((prev) => prev.map((tab) =>
          tab.id === activeRequestTabId
            ? { ...tab, ...requestData, id: tab.id, hasUnsavedChanges: false }
            : tab
        ));

        toastT('toast.requestSaved', 'success', { name: updatedRequest.name });
      } catch (error) {
        console.error('Failed to save request:', error);
        toastT('toast.saveRequestFailed', 'error', { message: error.message });
      }
    } else if (collections.length > 0) {
      // Saved tab whose backend request vanished (e.g. deleted by a teammate
      // while this tab was open): say so instead of silently doing nothing.
      // Guarded on a non-empty collections tree — an empty tree right after
      // a workspace switch means the realtime resync hasn't landed yet, not
      // that the request was actually deleted (see resolvedRequest above).
      toastT('toast.requestNoLongerExists', 'warning');
    }
  };

  return { selectRequest, saveRequest };
}
